// File: components/ProcessSectionDetails.client.tsx (CLIENT)
"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import { motion, type MotionProps } from "framer-motion";
import {
    Button,
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@relume_io/relume-ui";
import type { ButtonProps } from "@relume_io/relume-ui";
import CtaButton from "@/components/ui/CtaButton";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";

export type TimelineItem = { date: string; description: string };

type BaseProps = {
    heading: string;
    description: string;
    timelineItems: TimelineItem[];
    buttons?: ButtonProps[];
    useLandingButton?: boolean;
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type ProcessSectionDetailsClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.8,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
};

export default function ProcessSectionDetailsClient({
                                                        heading,
                                                        description,
                                                        timelineItems,
                                                        buttons = [],
                                                        useLandingButton = false,
                                                        className,
                                                        ...rest
                                                    }: ProcessSectionDetailsClientProps) {
    const [api, setApi] = useState<CarouselApi>();
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);
    const [slidesToScroll, setSlidesToScroll] = useState(1);

    useEffect(() => {
        const updateSlidesToScroll = () => {
            if (typeof window === "undefined") return;
            setSlidesToScroll(window.innerWidth >= 640 ? 2 : 1);
        };
        updateSlidesToScroll();
        window.addEventListener("resize", updateSlidesToScroll);
        return () => window.removeEventListener("resize", updateSlidesToScroll);
    }, []);

    useEffect(() => {
        if (!api) return;

        const updateArrows = () => {
            setCanPrev(api.canScrollPrev());
            setCanNext(api.canScrollNext());
        };

        api.on("select", updateArrows);
        api.on("reInit", updateArrows);

        api.scrollTo(0);
        api.reInit();
        updateArrows();

        return () => {
            api.off("select", updateArrows);
            api.off("reInit", updateArrows);
        };
    }, [api]);

    return (
        <motion.section
            id="relume"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className={["overflow-x-hidden overflow-y-visible px-[5%] py-16 md:py-24 lg:py-28", className]
                .filter(Boolean)
                .join(" ")}
            {...rest}
        >
            <div className="container">
                <div className="mb-12 md:mb-18 lg:mb-20">
                    <div className="mx-auto w-full max-w-lg text-center">
                        <motion.h2 variants={slideUp} custom={0} className="mb-5 text-4xl font-medium md:mb-6 md:text-6xl">
                            {heading}
                        </motion.h2>

                        <motion.p variants={slideUp} custom={1} className="md:text-md max-w-md mx-auto mb-8">
                            {description}
                        </motion.p>

                        {useLandingButton ? (
                            <ButtonLandingPage label="NAPISZ DO NAS" />
                        ) : (
                            <CtaButton />
                        )}

                        {buttons.length > 0 && (
                            <motion.div variants={slideUp} custom={2} className="mt-6 flex items-center justify-center gap-x-4 md:mt-8">
                                {buttons.map((button, i) => (
                                    <Button key={i} {...button}>
                                        {button.title}
                                    </Button>
                                ))}
                            </motion.div>
                        )}
                    </div>
                </div>

                <motion.div variants={slideUp} custom={3}>
                    <Carousel setApi={setApi} className="relative h-full overflow-visible pt-16" opts={{ slidesToScroll }}>
                        <div className="absolute left-0 top-1.5 z-20 h-1 w-16 bg-gradient-to-r to-transparent" />

                        <CarouselContent className="ml-0">
                            {timelineItems.map((item, index) => (
                                <CarouselItem key={index} className="basis-full pl-0 sm:basis-1/2 md:basis-1/2 lg:basis-1/2">
                                    <TimelineCard date={item.date} description={item.description} />
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        {canPrev && (
                            <CarouselPrevious className="size-10 rounded-full border bg-primary-light border-primary-light hover:bg-accent hover:text-primary-light transition duration-500" />
                        )}
                        {canNext && (
                            <CarouselNext className="size-10 rounded-full border bg-primary-light border-primary-light hover:bg-accent hover:text-primary-light transition duration-500" />
                        )}

                        <div className="absolute right-0 top-1.5 z-20 h-1 w-16 bg-gradient-to-l to-transparent" />
                    </Carousel>
                </motion.div>
            </div>
        </motion.section>
    );
}

function TimelineCard({ date, description }: { date: string; description: string }) {
    const [rawNumber, ...rest] = date.split(" ");
    const stepNumber = (rawNumber || "").replace(/\D/g, "").padStart(2, "0") || rawNumber;
    const title = rest.join(" ") || date;

    return (
        <div className="mb-8 flex w-full flex-col items-center md:mb-0 md:w-auto">
            <div className="relative mb-6 flex w-full items-center">
                <div className="h-[2px] w-full bg-black/10" />
                <div className="relative z-20 flex-none">
                    <div className="size-[1rem] rounded-full bg-accent" />
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium tracking-[0.16em] uppercase">
            Krok {stepNumber}
          </span>
                </div>
                <div className="h-[2px] w-full bg-black/10" />
            </div>

            <div className="mx-auto max-w-sm px-4 text-center md:px-6">
                <h3 className="mb-2 text-xl font-medium md:text-3xl ">{title}</h3>
                <p className="text-sm md:text-base max-w-xs mx-auto text-black/70">{description}</p>
            </div>
        </div>
    );
}